import { useTranslation } from "react-i18next";
import { useFormContext, Controller } from "react-hook-form";
import {
  Box,
  FormControl,
  FormControlLabel,
  FormHelperText,
  FormLabel,
  MenuItem,
  Select,
  Switch,
  TextField,
} from "@mui/material";

import { Entity } from "../../../../../../../types/types/entity.types";
import { languageShortsList } from "../../../../../../../enums/common.enum";
import { CountryAutocomplete, EntityAutocomplete } from "../../../../../../../shared/Form";

import { UserRoleEnum } from "../../../../../../User/enums";

import { introductoryVideoLengthList } from "../../../../../enum/jobEnums";
import { CreateProfessionalJob } from "../../../../../types/createJobSchema";
import CreateJobAccordion from "../../../../../components/CreateJobAccordion";

const RequiredSubmissionInfo = () => {
  const { t } = useTranslation("goHire", { keyPrefix: "createJobPage.jobDetails" });
  const { t: tLanguage } = useTranslation("goHire", { keyPrefix: "enum.language" });
  const { t: tIntroductoryVideoLength } = useTranslation("goHire", { keyPrefix: "enum.introductoryVideoLength" });

  const {
    control,
    watch,
    formState: { errors },
  } = useFormContext<CreateProfessionalJob>();

  const position = watch("position");
  const isLanguagesSpokenActive = watch("isLanguagesSpokenActive");
  const isIntroductoryVideoActive = watch("isIntroductoryVideoActive");
  const isClubsWorkedInActive = watch("isClubsWorkedInActive");

  const isCountriesScoutedInActive = watch("isCountriesScoutedInActive");

  const isCertificateAndLicensesActive = watch("isCertificateAndLicensesActive");
  const isCountriesCoachedInActive = watch("isCountriesCoachedInActive");

  const certificateAndLicensesList = [
    { label: "Certification 1", value: "1" },
    { label: "Certification 2", value: "2" },
    { label: "Certification 3", value: "3" },
  ];

  const hasError = Boolean(
    errors.languagesSpoken ||
    errors.willingnessToRelocate ||
    errors.introductoryVideo?.description ||
    errors.introductoryVideo?.length ||
    errors.clubsWorkedIn,
  );

  return (
    <CreateJobAccordion title={t("requiredSubmissionInfo")} error={hasError}>
      <FormControl sx={styles.formControl} fullWidth component="fieldset" variant="standard">
        <Controller
          name="isLanguagesSpokenActive"
          control={control}
          render={({ field: isLanguagesSpokenField }) => (
            <FormControlLabel
              sx={styles.switchLabel}
              control={<Switch {...isLanguagesSpokenField} />}
              label={t("languagesSpoken")}
            />
          )}
        />
        {isLanguagesSpokenActive && (
          <Controller
            name="languagesSpoken"
            control={control}
            render={({ field: languagesSpokenField, fieldState: { error: languagesSpokenError } }) => (
              <Box sx={styles.formSelectContainer}>
                <FormLabel error={!!languagesSpokenError}>{t("selectPreferredLanguages")}</FormLabel>
                <Select
                  {...languagesSpokenField}
                  value={languagesSpokenField.value || []}
                  onChange={(e) => languagesSpokenField.onChange(e.target.value)}
                  fullWidth
                  error={!!languagesSpokenError}
                  variant="outlined"
                  multiple
                  renderValue={(selected) => (selected as string[]).map((val) => tLanguage(val)).join(", ")}
                  sx={{
                    "& .MuiSelect-multiple": {
                      width: "100%",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    },
                  }}
                >
                  {languageShortsList.map((value) => (
                    <MenuItem key={value} value={value}>
                      {tLanguage(value)}
                    </MenuItem>
                  ))}
                </Select>
                {languagesSpokenError && <FormHelperText error>{t(languagesSpokenError.message)}</FormHelperText>}
              </Box>
            )}
          />
        )}
      </FormControl>

      <FormControl sx={formContainerStyles} fullWidth component="fieldset" variant="standard">
        <Controller
          name="willingnessToRelocate"
          control={control}
          render={({ field }) => (
            <FormControlLabel
              sx={styles.switchLabel}
              control={<Switch {...field} />}
              label={t("willingnessToRelocate")}
            />
          )}
        />
      </FormControl>

      <FormControl sx={styles.formControl} fullWidth component="fieldset" variant="standard">
        <Controller
          name="isIntroductoryVideoActive"
          control={control}
          render={({ field: isIntroductoryVideoActiveField }) => (
            <FormControlLabel
              sx={styles.switchLabel}
              control={<Switch {...isIntroductoryVideoActiveField} />}
              label={t("submitIntroductoryVideo")}
            />
          )}
        />
        {isIntroductoryVideoActive && (
          <>
            <Controller
              name="introductoryVideo.description"
              control={control}
              render={({ field, fieldState: { error } }) => (
                <Box sx={styles.formSelectContainer}>
                  <FormLabel error={!!error}>{t("describeTheVideoContent")}</FormLabel>
                  <TextField
                    {...field}
                    multiline
                    rows={5}
                    fullWidth
                    placeholder={t("writeDescription")}
                    error={!!error}
                    helperText={error?.message && t(error.message)}
                  />
                </Box>
              )}
            />
            <Controller
              name="introductoryVideo.length"
              control={control}
              render={({ field, fieldState: { error } }) => (
                <Box sx={styles.formSelectContainer}>
                  <FormLabel error={!!error}>{t("selectPreferredVideoLength")}</FormLabel>
                  <Select {...field} fullWidth error={!!error} variant="outlined">
                    <MenuItem value="" disabled>
                      {t("selectVideoLength")}
                    </MenuItem>
                    {introductoryVideoLengthList.map((value) => (
                      <MenuItem key={value} value={value}>
                        {tIntroductoryVideoLength(value)}
                      </MenuItem>
                    ))}
                  </Select>
                  {error && <FormHelperText error>{t(error.message)}</FormHelperText>}
                </Box>
              )}
            />
          </>
        )}
      </FormControl>

      <FormControl sx={styles.formControl} fullWidth component="fieldset" variant="standard">
        <Controller
          name="isClubsWorkedInActive"
          control={control}
          render={({ field }) => (
            <FormControlLabel
              sx={styles.switchLabel}
              control={<Switch {...field} />}
              label={t("specifyClubsAcademiesWorkedIn")}
            />
          )}
        />
        {isClubsWorkedInActive && (
          <Controller
            name="clubsWorkedIn"
            control={control}
            render={({ field, fieldState: { error } }) => (
              <Box sx={styles.formSelectContainer}>
                <FormLabel error={!!error}>{t("selectPreferredClubs*")}</FormLabel>
                <EntityAutocomplete
                  value={(field.value as Entity[] | null) || []}
                  onChange={(value) => field.onChange(value)}
                  multiple
                  error={!!error}
                />
                {error && <FormHelperText error>{t(error.message)}</FormHelperText>}
              </Box>
            )}
          />
        )}
      </FormControl>

      {position === UserRoleEnum.SCOUT && (
        <FormControl sx={styles.formControl} fullWidth component="fieldset" variant="standard">
          <Controller
            name="isCountriesScoutedInActive"
            control={control}
            render={({ field }) => (
              <FormControlLabel
                sx={styles.switchLabel}
                control={<Switch {...field} />}
                label={t("countriesScoutedIn")}
              />
            )}
          />
          {isCountriesScoutedInActive && (
            <Controller
              name="countriesScoutedIn"
              control={control}
              render={({ field, fieldState: { error } }) => (
                <Box sx={styles.formSelectContainer}>
                  <FormLabel error={!!error}>{t("selectCountriesScoutedIn*")}</FormLabel>
                  <CountryAutocomplete
                    value={field.value || []}
                    onChange={(value) => field.onChange(value)}
                    multiple
                    error={!!error}
                  />
                  {error && <FormHelperText error>{t(error.message)}</FormHelperText>}
                </Box>
              )}
            />
          )}
        </FormControl>
      )}

      {position === UserRoleEnum.COACH && (
        <>
          <FormControl sx={styles.formControl} fullWidth component="fieldset" variant="standard">
            <Controller
              name="isCountriesCoachedInActive"
              control={control}
              render={({ field }) => (
                <FormControlLabel
                  sx={styles.switchLabel}
                  control={<Switch {...field} />}
                  label={t("countriesCoachedIn")}
                />
              )}
            />
            {isCountriesCoachedInActive && (
              <Controller
                name="countriesCoachedIn"
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <Box sx={styles.formSelectContainer}>
                    <FormLabel error={!!error}>{t("selectCountriesCoachedIn*")}</FormLabel>
                    <CountryAutocomplete
                      value={field.value || []}
                      onChange={(value) => field.onChange(value)}
                      multiple
                      error={!!error}
                    />
                    {error && <FormHelperText error>{t(error.message)}</FormHelperText>}
                  </Box>
                )}
              />
            )}
          </FormControl>
          <FormControl sx={styles.formControl} fullWidth component="fieldset" variant="standard">
            <Controller
              name="isCertificateAndLicensesActive"
              control={control}
              render={({ field }) => (
                <FormControlLabel
                  sx={styles.switchLabel}
                  control={<Switch {...field} />}
                  label={t("certificateAndLicenses")}
                />
              )}
            />
            {isCertificateAndLicensesActive && (
              <Controller
                name="certificateAndLicenses"
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <Box sx={styles.formSelectContainer}>
                    <FormLabel error={!!error}>{t("selectCertificateAndLicenses*")}</FormLabel>
                    <Select
                      {...field}
                      value={field.value || []}
                      onChange={(e) => field.onChange(e.target.value)}
                      fullWidth
                      error={!!error}
                      variant="outlined"
                      multiple
                    >
                      {certificateAndLicensesList.map(({ value, label }) => (
                        <MenuItem key={value} value={value}>
                          {label}
                        </MenuItem>
                      ))}
                    </Select>
                    {error && <FormHelperText error>{t(error.message)}</FormHelperText>}
                  </Box>
                )}
              />
            )}
          </FormControl>
        </>
      )}

      {position === UserRoleEnum.AGENT && (
        <FormControl sx={styles.formControl} fullWidth component="fieldset" variant="standard">
          <Controller
            name="totalCareerTransfers"
            control={control}
            render={({ field, fieldState: { error } }) => (
              <FormControlLabel
                sx={styles.switchLabel}
                control={<Switch {...field} />}
                label={t("totalCareerTransfers")}
              />
            )}
          />
        </FormControl>
      )}
    </CreateJobAccordion>
  );
};

export default RequiredSubmissionInfo;

const formContainerStyles = {
  padding: "16px",
  borderRadius: "8px",
  border: "1px solid var(--Gray-200, #E4E7EC)",
  background: "var(--White, #FFF)",
};

const styles = {
  switchLabel: {
    margin: 0,
    flexDirection: "row-reverse",
    justifyContent: "space-between",
    color: "var(--Dark---1000, #111)",
    fontFamily: "Montserrat",
    fontSize: "16px",
    fontStyle: "normal",
    fontWeight: 600,
    lineHeight: "24px",
  },
  formControl: {
    width: "100%",
    flexDirection: "column",
    gap: "16px",
    ...formContainerStyles,
  },
  formSelectContainer: {
    display: "flex",
    flexDirection: "column",
    gap: "6px",
  },
};
